import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Calendar, Clock, Eye, Tag, ChevronRight, Search, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Blog List Page
const BlogList = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedTag) params.append('tag', selectedTag);
      params.append('limit', '50');

      const [articlesRes, categoriesRes, tagsRes] = await Promise.all([
        fetch(`${API_URL}/api/blog/public/articles?${params}`),
        fetch(`${API_URL}/api/blog/public/categories`),
        fetch(`${API_URL}/api/blog/public/tags`)
      ]);

      if (articlesRes.ok) {
        const data = await articlesRes.json();
        setArticles(data.articles || []);
      }
      if (categoriesRes.ok) {
        setCategories(await categoriesRes.json());
      }
      if (tagsRes.ok) {
        setTags(await tagsRes.json());
      }
    } catch (error) {
      console.error('Error fetching blog data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedTag]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredArticles = articles.filter(article =>
    article.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.excerpt?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="public-blog">
      {/* SEO Meta Tags for Social Media Sharing */}
      <Helmet>
        <title>FlowFeed ERP Blog | Business Tips, ERP Guides & Insights</title>
        <meta name="description" content="Expert insights on ERP implementation, CRM best practices, inventory management, and business growth. Free guides for SMBs." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://flowfeederp.com/blog" />
        <meta property="og:title" content="FlowFeed ERP Blog | Business Tips & ERP Guides" />
        <meta property="og:description" content="Expert insights on ERP implementation, CRM best practices, inventory management, and business growth." />
        <meta property="og:image" content="https://flowfeederp.com/og-image.svg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://flowfeederp.com/blog" />
        <meta name="twitter:title" content="FlowFeed ERP Blog | Business Tips & ERP Guides" />
        <meta name="twitter:description" content="Expert insights on ERP implementation, CRM best practices, inventory management, and business growth." />
        <meta name="twitter:image" content="https://flowfeederp.com/og-image.svg" />
        
        {/* LinkedIn */}
        <meta property="og:site_name" content="FlowFeed ERP" />
      </Helmet>

      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center gap-2" data-testid="blog-logo">
              <img src="/favicon.svg" alt="FlowFeed ERP" className="w-8 h-8" />
              <span className="font-bold text-xl text-slate-900">FlowFeed ERP</span>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-600 hover:text-blue-600 font-medium">Home</Link>
              <Link to="/blog" className="text-blue-600 font-medium">Blog</Link>
              <Link to="/" className="text-slate-600 hover:text-blue-600 font-medium">Contact</Link>
              <Link 
                to="/login" 
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition"
                data-testid="blog-login-btn"
              >
                Login
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="blog-title">
            FlowFeed ERP Blog
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Expert insights, tips, and guides to help you grow your business with smart ERP solutions
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Articles Grid */}
          <div className="flex-1">
            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="blog-search"
                />
              </div>
            </div>

            {/* Active Filters */}
            {(selectedCategory || selectedTag) && (
              <div className="mb-6 flex items-center gap-2 flex-wrap">
                <span className="text-slate-600">Filters:</span>
                {selectedCategory && (
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center gap-1 hover:bg-blue-200"
                  >
                    {selectedCategory} ×
                  </button>
                )}
                {selectedTag && (
                  <button
                    onClick={() => setSelectedTag(null)}
                    className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm flex items-center gap-1 hover:bg-purple-200"
                  >
                    {selectedTag} ×
                  </button>
                )}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-slate-600 text-lg">No articles found</p>
              </div>
            ) : (
              <div className="grid gap-8" data-testid="articles-grid">
                {/* Featured Article */}
                {filteredArticles[0] && (
                  <article 
                    className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition cursor-pointer"
                    onClick={() => navigate(`/blog/${filteredArticles[0].slug}`)}
                    data-testid="featured-article"
                  >
                    <div className="md:flex">
                      <div className="md:w-2/5">
                        <img
                          src={filteredArticles[0].featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop'}
                          alt={filteredArticles[0].title}
                          className="w-full h-64 md:h-full object-cover"
                        />
                      </div>
                      <div className="md:w-3/5 p-8">
                        <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                          {filteredArticles[0].category}
                        </span>
                        <h2 className="text-2xl font-bold text-slate-900 mb-3 hover:text-blue-600 transition">
                          {filteredArticles[0].title}
                        </h2>
                        <p className="text-slate-600 mb-4 line-clamp-3">
                          {filteredArticles[0].excerpt}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {filteredArticles[0].author_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(filteredArticles[0].published_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="h-4 w-4" />
                            {filteredArticles[0].views || 0} views
                          </span>
                        </div>
                      </div>
                    </div>
                  </article>
                )}

                {/* Other Articles */}
                <div className="grid md:grid-cols-2 gap-6">
                  {filteredArticles.slice(1).map((article) => (
                    <article
                      key={article.slug}
                      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition cursor-pointer"
                      onClick={() => navigate(`/blog/${article.slug}`)}
                      data-testid={`article-card-${article.slug}`}
                    >
                      <img
                        src={article.featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop'}
                        alt={article.title}
                        className="w-full h-48 object-cover"
                      />
                      <div className="p-6">
                        <span className="inline-block bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs font-medium mb-3">
                          {article.category}
                        </span>
                        <h3 className="text-lg font-semibold text-slate-900 mb-2 line-clamp-2 hover:text-blue-600 transition">
                          {article.title}
                        </h3>
                        <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(article.published_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {article.views || 0}
                          </span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:w-80">
            {/* Categories */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6" data-testid="categories-sidebar">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-1 h-5 bg-blue-600 rounded"></span>
                Categories
              </h3>
              <ul className="space-y-2">
                {categories.map((cat) => (
                  <li key={cat.name}>
                    <button
                      onClick={() => setSelectedCategory(cat.name === selectedCategory ? null : cat.name)}
                      className={`w-full text-left px-3 py-2 rounded-lg flex justify-between items-center transition ${
                        selectedCategory === cat.name
                          ? 'bg-blue-100 text-blue-700'
                          : 'hover:bg-slate-100 text-slate-700'
                      }`}
                    >
                      <span>{cat.name}</span>
                      <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded text-xs">
                        {cat.count}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Tags */}
            <div className="bg-white rounded-xl shadow-md p-6" data-testid="tags-sidebar">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-1 h-5 bg-purple-600 rounded"></span>
                Popular Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {tags.slice(0, 15).map((tag) => (
                  <button
                    key={tag.name}
                    onClick={() => setSelectedTag(tag.name === selectedTag ? null : tag.name)}
                    className={`px-3 py-1 rounded-full text-sm transition ${
                      selectedTag === tag.name
                        ? 'bg-purple-600 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl p-6 mt-6 text-white">
              <h3 className="font-semibold text-lg mb-2">Ready to transform your business?</h3>
              <p className="text-blue-100 text-sm mb-4">
                Start your free trial today and see why 1000+ businesses trust FlowFeed ERP.
              </p>
              <Link
                to="/signup"
                className="block text-center bg-white text-blue-600 font-semibold py-2 rounded-lg hover:bg-blue-50 transition"
              >
                Get Started Free
              </Link>
            </div>
          </aside>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img src="/favicon.svg" alt="FlowFeed ERP" className="w-8 h-8" />
              <span className="font-bold text-xl">FlowFeed ERP</span>
            </div>
            <div className="flex gap-6 text-slate-400">
              <Link to="/" className="hover:text-white transition">Home</Link>
              <Link to="/" className="hover:text-white transition">About</Link>
              <Link to="/" className="hover:text-white transition">FAQ</Link>
              <Link to="/" className="hover:text-white transition">Contact</Link>
              <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-500 text-sm">
            © 2025 FlowFeed ERP Solutions. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

// Single Article Page
const BlogArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    
    const fetchArticle = async () => {
      if (!slug) {
        navigate('/blog');
        return;
      }
      
      try {
        const url = `${API_URL}/api/blog/public/articles/${slug}`;
        const response = await fetch(url);
        
        if (!isMounted) return;
        
        if (response.ok) {
          const data = await response.json();
          setArticle(data);
          setError(null);
        } else {
          setError('Article not found');
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchArticle();
    
    return () => {
      isMounted = false;
    };
  }, [slug, navigate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Simple markdown-like rendering
  const renderContent = (content) => {
    if (!content) return null;
    
    return content.split('\n').map((line, i) => {
      // Headers
      if (line.startsWith('### ')) {
        return <h3 key={i} className="text-xl font-semibold mt-8 mb-4 text-slate-900">{line.slice(4)}</h3>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={i} className="text-2xl font-bold mt-10 mb-4 text-slate-900">{line.slice(3)}</h2>;
      }
      if (line.startsWith('# ')) {
        return <h1 key={i} className="text-3xl font-bold mt-10 mb-4 text-slate-900">{line.slice(2)}</h1>;
      }
      // Lists
      if (line.startsWith('- ')) {
        return <li key={i} className="ml-6 text-slate-700">{line.slice(2)}</li>;
      }
      // Bold text
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} className="font-semibold text-slate-900 mt-4">{line.slice(2, -2)}</p>;
      }
      // Links
      if (line.includes('[') && line.includes('](')) {
        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        const parts = [];
        let lastIndex = 0;
        let match;
        
        while ((match = linkRegex.exec(line)) !== null) {
          if (match.index > lastIndex) {
            parts.push(line.slice(lastIndex, match.index));
          }
          parts.push(
            <Link key={match.index} to={match[2]} className="text-blue-600 hover:underline">
              {match[1]}
            </Link>
          );
          lastIndex = match.index + match[0].length;
        }
        if (lastIndex < line.length) {
          parts.push(line.slice(lastIndex));
        }
        return <p key={i} className="text-slate-700 my-2">{parts}</p>;
      }
      // Empty lines
      if (!line.trim()) {
        return <br key={i} />;
      }
      // Regular paragraphs
      return <p key={i} className="text-slate-700 my-2 leading-relaxed">{line}</p>;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Article Not Found</h1>
        <p className="text-slate-600 mb-6">{error || 'The article you are looking for does not exist.'}</p>
        <Link to="/blog" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">
          Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="blog-article-page">
      {/* SEO Meta Tags for Social Media Sharing */}
      <Helmet>
        <title>{article.meta_title || article.title} | FlowFeed ERP</title>
        <meta name="description" content={article.meta_description || article.excerpt} />
        
        {/* Open Graph / Facebook / LinkedIn */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://flowfeederp.com/blog/${article.slug}`} />
        <meta property="og:title" content={article.meta_title || article.title} />
        <meta property="og:description" content={article.meta_description || article.excerpt} />
        <meta property="og:image" content={article.featured_image || 'https://flowfeederp.com/og-image.svg'} />
        <meta property="og:site_name" content="FlowFeed ERP" />
        <meta property="article:published_time" content={article.published_at} />
        <meta property="article:author" content={article.author_name} />
        <meta property="article:section" content={article.category} />
        {article.tags?.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`https://flowfeederp.com/blog/${article.slug}`} />
        <meta name="twitter:title" content={article.meta_title || article.title} />
        <meta name="twitter:description" content={article.meta_description || article.excerpt} />
        <meta name="twitter:image" content={article.featured_image || 'https://flowfeederp.com/og-image.svg'} />
      </Helmet>

      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center gap-2">
              <img src="/favicon.svg" alt="FlowFeed ERP" className="w-8 h-8" />
              <span className="font-bold text-xl text-slate-900">FlowFeed ERP</span>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-600 hover:text-blue-600 font-medium">Home</Link>
              <Link to="/blog" className="text-blue-600 font-medium">Blog</Link>
              <Link to="/" className="text-slate-600 hover:text-blue-600 font-medium">Contact</Link>
              <Link to="/login" className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition">
                Login
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/blog" className="hover:text-blue-600">Blog</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-slate-900 truncate">{article.title}</span>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate('/blog')}
          className="flex items-center gap-2 text-slate-600 hover:text-blue-600 mb-8 transition"
          data-testid="back-to-blog"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Blog
        </button>

        {/* Header */}
        <header className="mb-10">
          <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
            {article.category}
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-testid="article-title">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-slate-600">
            <span className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {article.author_name}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {formatDate(article.published_at)}
            </span>
            <span className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              {article.views || 0} views
            </span>
          </div>
        </header>

        {/* Featured Image */}
        {article.featured_image && (
          <img
            src={article.featured_image}
            alt={article.title}
            className="w-full h-auto rounded-2xl mb-10 shadow-lg"
            data-testid="article-image"
          />
        )}

        {/* Content */}
        <div className="prose prose-lg max-w-none" data-testid="article-content">
          {renderContent(article.content)}
        </div>

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="h-5 w-5 text-slate-500" />
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/blog?tag=${tag}`}
                  className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm hover:bg-slate-200 transition"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Social Share Buttons */}
        <div className="mt-8 pt-8 border-t border-slate-200">
          <h4 className="text-sm font-semibold text-slate-700 mb-4">Share this article:</h4>
          <div className="flex gap-3">
            {/* LinkedIn */}
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://flowfeederp.com/blog/${article.slug}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#0077B5] text-white px-4 py-2 rounded-lg hover:bg-[#006399] transition"
              data-testid="share-linkedin"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              LinkedIn
            </a>
            
            {/* Twitter/X */}
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(`https://flowfeederp.com/blog/${article.slug}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition"
              data-testid="share-twitter"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              Twitter
            </a>
            
            {/* Facebook */}
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://flowfeederp.com/blog/${article.slug}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#1877F2] text-white px-4 py-2 rounded-lg hover:bg-[#166FE5] transition"
              data-testid="share-facebook"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              Facebook
            </a>
            
            {/* WhatsApp */}
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`${article.title} - https://flowfeederp.com/blog/${article.slug}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-lg hover:bg-[#20BD5A] transition"
              data-testid="share-whatsapp"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </a>
            
            {/* Copy Link */}
            <button
              onClick={() => {
                navigator.clipboard.writeText(`https://flowfeederp.com/blog/${article.slug}`);
                alert('Link copied to clipboard!');
              }}
              className="flex items-center gap-2 bg-slate-600 text-white px-4 py-2 rounded-lg hover:bg-slate-700 transition"
              data-testid="share-copy"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
              Copy Link
            </button>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-3">Ready to streamline your business?</h3>
          <p className="text-blue-100 mb-6 max-w-xl mx-auto">
            Join thousands of businesses using FlowFeed ERP to manage their operations efficiently.
          </p>
          <div className="flex justify-center gap-4">
            <Link
              to="/signup"
              className="bg-white text-blue-600 font-semibold px-6 py-3 rounded-lg hover:bg-blue-50 transition"
            >
              Start Free Trial
            </Link>
            <Link
              to="/"
              className="border border-white text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/10 transition"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img src="/favicon.svg" alt="FlowFeed ERP" className="w-8 h-8" />
              <span className="font-bold text-xl">FlowFeed ERP</span>
            </div>
            <div className="flex gap-6 text-slate-400">
              <Link to="/" className="hover:text-white transition">Home</Link>
              <Link to="/" className="hover:text-white transition">About</Link>
              <Link to="/" className="hover:text-white transition">FAQ</Link>
              <Link to="/" className="hover:text-white transition">Contact</Link>
              <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-500 text-sm">
            © 2025 FlowFeed ERP Solutions. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

// Main component that handles routing
const PublicBlog = () => {
  return <BlogList />;
};

// Export both components for routing
export { BlogArticle };
export default PublicBlog;
